import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { Fragment, forwardRef, useEffect, useRef } from 'react';
import { Box, Typography, FormControlLabel, Checkbox, TextField, InputAdornment, Select, MenuItem, Autocomplete } from '@mui/material';
import CustomSelect2 from './CustomSelect2';
import SelectPlanCheckbox from './SelectPlanCheckbox';
import PercentInput from './PercentInput';
import NotesDisplay from './NotesDisplay';
import RatesVersionSelect from './RatesVersionSelect';
import CopyImageButton from './CopyImageButton';
import CopyExcelDataButton from './CopyExcelDataButton';
import TableCellRate from './TableCellRate';
import TableCellText from './TableCellText';
import TableRatesRow from './TableRatesRow';
import countries from './Countries_Data';
import nationalityToCountry from '../utils/nationalityToCountry';

const CignaGlobalHealthOption_Rates = forwardRef(({ appConfig, sx, clients, loadingStatus, currency, selectedPlans, setSelectedPlans,
  response, deductibles, setDeductibles, addToPendingTasks,
  discounts, setDiscounts, loadings, setLoadings, computedRates, setComputedRates,
  specificAreaOfCoverage, setSpecificAreaOfCoverage, areaOfCoverage,
  setShowImageCopy, selectedOptions, addSelectedOption,
  setViewSelectedOption, specificCurrency, setSpecificCurrency,
  specificPaymentFrequency, setSpecificPaymentFrequency,
  cignaGlobalHealthOptionSpecific, setCignaGlobalHealthOptionSpecific,
  insurerRates = [], selectedRatesVersion, setSelectedRatesVersion, handleFetchAppConfig, setShowRefreshNow }, ref) => {

  const id = 'cigna_global_health_option';

  const containerRef = useRef(null);

  const config = appConfig?.content?.specifics?.cigna_global_health_option || {};

  const inpatientCostShareByCurrency = config['Inpatient Cost Share'] || {};
  const outpatientDeductibleByCurrency = config['Outpatient Deductible'] || {};
  const outpatientCostShareByCurrency = config['Outpatient Cost Share'] || {};
  const paymentFrequencyRate = config['Payment Frequency Rate'] || {};

  const deductiblesByCurrency = appConfig?.content?.deductibles?.cigna_global_health_option || {};

  const currencyOptions = Object.keys(deductiblesByCurrency).length > 0 ? Object.keys(deductiblesByCurrency) : ['USD', 'EURO', 'GBP'];
  const paymentFrequencyOptions = Object.keys(paymentFrequencyRate).length > 0 ? Object.keys(paymentFrequencyRate) : ['Monthly', 'Quarterly', 'Annually'];

  const AREA_OF_COVERAGE_WITH_USA = 'Worldwide incl USA';
  const AREA_OF_COVERAGE_WITHOUT_USA = 'Worldwide excl USA';

  const NO_RATE = '-';

  // Tiers own the IP/OP rate and the OtherMods (Health & Wellbeing / Vision & Dental /
  // Medical Evacuation) riders. IP and OP field names lead with the module, the riders
  // lead with the tier - matches the response field naming from the rate provider.
  const tiers = [
    { tierId: 'silver', label: 'Silver' },
    { tierId: 'gold', label: 'Gold' },
    { tierId: 'platinum', label: 'Platinum' }
  ];

  // Inpatient is always included in the premium - it can't be unchecked like the other,
  // optional riders.
  const MANDATORY_CATEGORY_KEY = 'ip';

  const categories = [
    { key: 'ip', label: 'International Inpatient', field: (tier) => `ip_${tier}` },
    { key: 'op', label: 'International Outpatient', field: (tier) => `op_${tier}` },
    { key: 'medical_evacuation', label: 'International Medical Evacuation', field: (tier) => `${tier}_medical_evacuation` },
    { key: 'health_and_wellbeing', label: 'International Health & Wellbeing', field: (tier) => `${tier}_health_and_wellbeing` },
    { key: 'vision_and_dental', label: 'International Vision & Dental', field: (tier) => `${tier}_vision_and_dental` }
  ];

  // Gold/Silver/Platinum start checked whenever this insurer is turned on - the user can
  // still uncheck them afterwards.
  useEffect(() => {
    const missingTierPlanIds = tiers.map(({ tierId }) => `${id}_${tierId}`).filter(planId => !selectedPlans.includes(planId));

    if (missingTierPlanIds.length > 0) {
      setSelectedPlans([...selectedPlans, ...missingTierPlanIds]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    computedRates[id] = getComputedRates();
    setComputedRates(computedRates);
  }, [response, discounts[id], loadings[id], selectedPlans, clients.length]);

  useEffect(() => {
    setTimeout(() => {
      addSelectedOption(id, 'specific_area_of_coverage', specificAreaOfCoverage[id]);
      addSelectedOption(id, 'specific_currency', specificCurrency[id]);
      addSelectedOption(id, 'specific_payment_frequency', specificPaymentFrequency[id]);
      addSelectedOption(id, 'deductible', deductibles[id]);
      addSelectedOption(id, 'inpatient_cost_share', cignaGlobalHealthOptionSpecific['inpatient_cost_share']);
      addSelectedOption(id, 'outpatient_deductible', cignaGlobalHealthOptionSpecific['outpatient_deductible']);
      addSelectedOption(id, 'outpatient_cost_share', cignaGlobalHealthOptionSpecific['outpatient_cost_share']);
      addSelectedOption(id, 'contracting_country', cignaGlobalHealthOptionSpecific['contracting_country']);
      addSelectedOption(id, 'discount', discounts[id] == '' ? 0 : discounts[id]);
      addSelectedOption(id, 'loading', loadings[id] == '' ? 0 : loadings[id]);
    }, 1000);
  }, [specificAreaOfCoverage[id], specificCurrency[id], specificPaymentFrequency[id], deductibles[id],
  cignaGlobalHealthOptionSpecific['inpatient_cost_share'], cignaGlobalHealthOptionSpecific['outpatient_deductible'],
  cignaGlobalHealthOptionSpecific['outpatient_cost_share'], cignaGlobalHealthOptionSpecific['contracting_country'],
  discounts[id], loadings[id]]);

  useEffect(() => {
    setViewSelectedOption(id, 'specific_area_of_coverage', (option_value) => {
      specificAreaOfCoverage[id] = option_value;
      setSpecificAreaOfCoverage(specificAreaOfCoverage);
    });

    setViewSelectedOption(id, 'specific_currency', (option_value) => {
      specificCurrency[id] = option_value;
      setSpecificCurrency(specificCurrency);
    });

    setViewSelectedOption(id, 'specific_payment_frequency', (option_value) => {
      specificPaymentFrequency[id] = option_value;
      setSpecificPaymentFrequency(specificPaymentFrequency);
    });

    setViewSelectedOption(id, 'deductible', (option_value) => {
      setDeductibles({ ...deductibles, [id]: option_value });
    });

    setViewSelectedOption(id, 'inpatient_cost_share', (option_value) => {
      cignaGlobalHealthOptionSpecific['inpatient_cost_share'] = option_value;
      setCignaGlobalHealthOptionSpecific({ ...cignaGlobalHealthOptionSpecific });
    });

    setViewSelectedOption(id, 'outpatient_deductible', (option_value) => {
      cignaGlobalHealthOptionSpecific['outpatient_deductible'] = option_value;
      setCignaGlobalHealthOptionSpecific({ ...cignaGlobalHealthOptionSpecific });
    });

    setViewSelectedOption(id, 'outpatient_cost_share', (option_value) => {
      cignaGlobalHealthOptionSpecific['outpatient_cost_share'] = option_value;
      setCignaGlobalHealthOptionSpecific({ ...cignaGlobalHealthOptionSpecific });
    });

    setViewSelectedOption(id, 'contracting_country', (option_value) => {
      cignaGlobalHealthOptionSpecific['contracting_country'] = option_value;
      setCignaGlobalHealthOptionSpecific({ ...cignaGlobalHealthOptionSpecific });
    });

    setViewSelectedOption(id, 'discount', (option_value) => {
      discounts[id] = option_value;
      setDiscounts(discounts);
    });

    setViewSelectedOption(id, 'loading', (option_value) => {
      loadings[id] = option_value;
      setLoadings(loadings);
    });
  }, [selectedOptions]);

  function getComputedRates() {
    if (!response) return;

    const rates = response.rates;

    const _computedRates = [];

    for (let i = 0; i < rates.length; i++) {
      const computedRate = {
        'client': rates[i].client,
        'currency': rates[i].currency
      };

      tiers.forEach(({ tierId }) => {
        computedRate[tierId] = getFinalPremium(i, tierId);
      });

      _computedRates.push(computedRate);
    }

    return _computedRates;
  }

  function getRate(clientIndex, field) {
    if (!response) return NO_RATE;

    if (!response.rates[clientIndex]) return NO_RATE;

    const rate = response.rates[clientIndex][field];

    if (rate === undefined || rate === null) return NO_RATE;

    return rate;
  }

  function isRateAvailable(rate) {
    return !(rate === '' || rate === null || rate === undefined || typeof rate === 'string' || isNaN(rate));
  }

  function getSelectedCategories() {
    return categories.filter(({ key }) => key === MANDATORY_CATEGORY_KEY || selectedPlans.includes(`${id}_${key}`));
  }

  // Sum of every selected benefit for this tier, before discount/loading - 0 when the
  // tier itself or no benefits are selected.
  function getPremium(clientIndex, tier) {
    if (!selectedPlans.includes(`${id}_${tier}`)) return 0;

    const _selectedCategories = getSelectedCategories();

    if (_selectedCategories.length === 0) return 0;

    let total = 0;

    for (let i = 0; i < _selectedCategories.length; i++) {
      const rate = getRate(clientIndex, _selectedCategories[i].field(tier));

      if (!isRateAvailable(rate)) return rate;

      total += rate;
    }

    return total;
  }

  function getRenewalPercent() {
    const discount = parseFloat(discounts[id]) || 0;
    const loading = parseFloat(loadings[id]) || 0;

    return (100 - discount + loading) * 0.01;
  }

  function getFinalPremium(clientIndex, tier) {
    const premium = getPremium(clientIndex, tier);

    if (!isRateAvailable(premium)) return premium;

    return premium * getRenewalPercent();
  }

  // International Inpatient is shown on the tier header row instead of its own row, so it
  // needs its own rate (mirrors getFinalPremium but for the ip category alone).
  function getInpatientFinalPremium(clientIndex, tier) {
    const inpatientField = categories.find(({ key }) => key === MANDATORY_CATEGORY_KEY).field(tier);
    const rate = getRate(clientIndex, inpatientField);

    if (!isRateAvailable(rate)) return rate;

    return rate * getRenewalPercent();
  }

  function getSubtotal(getRateForClient) {
    let subTotal = 0;

    for (let i = 0; i < clients.length; i++) {
      const rate = getRateForClient(i);

      if (!isRateAvailable(rate)) return rate;

      subTotal += rate;
    }

    return subTotal;
  }

  function getCurrency() {
    if (!response) return specificCurrency[id];

    if (!response.rates || response.rates.length === 0) return specificCurrency[id];

    return response.rates[0].currency;
  }

  function getAreaOfCoverage() {
    return specificAreaOfCoverage[id] || AREA_OF_COVERAGE_WITHOUT_USA;
  }

  // The rate provider only looks up a contracting country premium tax override for these
  // residences - every other residence just uses its own country's tax rate, so the field
  // stays hidden and unused for them.
  const CONTRACTING_COUNTRY_RESIDENCES = ['Australia', 'Canada', 'United States of America'];

  function requiresContractingCountry() {
    return clients.some(client => CONTRACTING_COUNTRY_RESIDENCES.includes(client.country_of_residence));
  }

  // e.g. a Filipino national residing in the Philippines - this combination can't be rated
  // and needs manual handling by sales support.
  function hasSameNationalityAndResidence() {
    return clients.some(client => {
      const nationalityCountry = nationalityToCountry(client.nationality);

      return nationalityCountry && nationalityCountry === client.country_of_residence;
    });
  }

  function getPremiumLabel() {
    const premiumLabels = {
      Annually: 'Annual Premium',
      Monthly: 'Monthly Premium',
      Quarterly: 'Quarterly Premium'
    };

    return premiumLabels[specificPaymentFrequency[id]] || 'Total Premium';
  }

  function handleCurrencyChange(newCurrency) {
    specificCurrency[id] = newCurrency;
    setSpecificCurrency({ ...specificCurrency });

    const newDeductibleOptions = deductiblesByCurrency[newCurrency] || [];
    const newInpatientCostShareOptions = inpatientCostShareByCurrency[newCurrency] || [];
    const newOutpatientDeductibleOptions = outpatientDeductibleByCurrency[newCurrency] || [];
    const newOutpatientCostShareOptions = outpatientCostShareByCurrency[newCurrency] || [];

    setDeductibles({ ...deductibles, [id]: newDeductibleOptions[0] || '' });

    setCignaGlobalHealthOptionSpecific({
      ...cignaGlobalHealthOptionSpecific,
      inpatient_cost_share: newInpatientCostShareOptions[0] || '',
      outpatient_deductible: newOutpatientDeductibleOptions[0] || '',
      outpatient_cost_share: newOutpatientCostShareOptions[0] || ''
    });

    addToPendingTasks([id]);
  }

  function handleSpecificChange(key, value) {
    setCignaGlobalHealthOptionSpecific({ ...cignaGlobalHealthOptionSpecific, [key]: value });

    addToPendingTasks([id]);
  }

  return (
    <Box id={id} sx={sx}>
      <Paper sx={{ borderColor: '#e0e0e0', borderWidth: 2, pb: 2, scrollMarginTop: '200px' }} variant='outlined' ref={containerRef}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#00629b', px: 2, py: 1 }}>
          <div style={{
            color: '#fff',
            backgroundColor: '#00629b',
            padding: 6,
            fontFamily: 'Poppins, sans-serif',
            fontSize: 18,
            lineHeight: 1.2,
            height: 60
          }}>Cigna Global Health Option</div>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, background: '#f8f9fa', px: 1.5, py: 0.5 }}>
            <img
              src={`${process.env.PUBLIC_URL}/images/cigna-logo.svg`}
              alt="Cigna"
              style={{ height: 55, userSelect: 'none', WebkitUserDrag: 'none' }}
              draggable={false}
              onContextMenu={(e) => e.preventDefault()}
            />
          </Box>
        </Box>
        <Box sx={{ p: 2 }}>

          <Box sx={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Plan Type</Typography>
              {
                tiers.map(({ tierId, label }) => (
                  <FormControlLabel
                    key={tierId}
                    sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={`${id}_${tierId}`} />}
                    label={label}
                  />
                ))
              }
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column' }}>
              <Typography fontSize={15} fontWeight={600}>Benefit</Typography>

              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<Checkbox size="small" checked onChange={(e) => e.preventDefault()} />}
                label={categories.find(({ key }) => key === 'ip').label}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 3.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={12} fontWeight={600} sx={{ width: 110 }}>Core Deductible</Typography>
                  <CustomSelect2
                    dense
                    sx={{ width: 210 }}
                    value={deductibles[id] || ''}
                    onChange={(e) => {
                      setDeductibles({ ...deductibles, [id]: e.target.value });

                      addToPendingTasks([id]);
                    }}
                    items={deductiblesByCurrency[specificCurrency[id]] || []}
                    disabled={loadingStatus[id]}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={12} fontWeight={600} sx={{ width: 110 }}>Cost Share after Deductible</Typography>
                  <CustomSelect2
                    dense
                    sx={{ width: 210 }}
                    value={cignaGlobalHealthOptionSpecific['inpatient_cost_share'] || ''}
                    onChange={(e) => handleSpecificChange('inpatient_cost_share', e.target.value)}
                    items={inpatientCostShareByCurrency[specificCurrency[id]] || []}
                    disabled={loadingStatus[id]}
                    isZeroDeductibleUniform={false}
                  />
                </Box>
              </Box>

              <FormControlLabel
                sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={`${id}_op`} />}
                label={categories.find(({ key }) => key === 'op').label}
              />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, pl: 3.5, mb: 1.5 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={12} fontWeight={600} sx={{ width: 110 }}>OP Deductible</Typography>
                  <CustomSelect2
                    dense
                    sx={{ width: 210 }}
                    value={cignaGlobalHealthOptionSpecific['outpatient_deductible'] || ''}
                    onChange={(e) => handleSpecificChange('outpatient_deductible', e.target.value)}
                    items={outpatientDeductibleByCurrency[specificCurrency[id]] || []}
                    disabled={loadingStatus[id]}
                  />
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography fontSize={12} fontWeight={600} sx={{ width: 110 }}>OP Cost Share</Typography>
                  <CustomSelect2
                    dense
                    sx={{ width: 210 }}
                    value={cignaGlobalHealthOptionSpecific['outpatient_cost_share'] || ''}
                    onChange={(e) => handleSpecificChange('outpatient_cost_share', e.target.value)}
                    items={outpatientCostShareByCurrency[specificCurrency[id]] || []}
                    disabled={loadingStatus[id]}
                    isZeroDeductibleUniform={false}
                  />
                </Box>
              </Box>

              {
                categories.filter(({ key }) => key !== 'ip' && key !== 'op').map(({ key, label }) => (
                  <FormControlLabel
                    key={key}
                    sx={{ mb: 0, '& .MuiFormControlLabel-label': { fontSize: 13 } }}
                    control={<SelectPlanCheckbox size="small" selectedPlans={selectedPlans} setSelectedPlans={setSelectedPlans} planId={`${id}_${key}`} />}
                    label={label}
                  />
                ))
              }
            </Box>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Area of Coverage</Typography>
                <CustomSelect2
                  dense
                  sx={{ width: 170 }}
                  value={getAreaOfCoverage()}
                  onChange={(e) => {
                    specificAreaOfCoverage[id] = e.target.value;
                    setSpecificAreaOfCoverage({ ...specificAreaOfCoverage });

                    addToPendingTasks([id]);
                  }}
                  items={[AREA_OF_COVERAGE_WITHOUT_USA, AREA_OF_COVERAGE_WITH_USA]}
                  disabled={loadingStatus[id]}
                  isZeroDeductibleUniform={false}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Currency</Typography>
                <Select
                  size='small'
                  value={specificCurrency[id] || 'USD'}
                  onChange={(e) => handleCurrencyChange(e.target.value)}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    currencyOptions.map(_currency => (
                      <MenuItem key={_currency} value={_currency} sx={{ fontSize: 12 }}>{_currency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Payment Frequency</Typography>
                <Select
                  size='small'
                  value={specificPaymentFrequency[id] || 'Annually'}
                  onChange={(e) => {
                    specificPaymentFrequency[id] = e.target.value;
                    setSpecificPaymentFrequency({ ...specificPaymentFrequency });

                    addToPendingTasks([id]);
                  }}
                  sx={{ fontSize: 12, height: 32, width: '170px', minWidth: 170 }}
                  fullWidth
                  disabled={loadingStatus[id]}
                >
                  {
                    paymentFrequencyOptions.map(payment_frequency => (
                      <MenuItem key={payment_frequency} value={payment_frequency} sx={{ fontSize: 12 }}>{payment_frequency}</MenuItem>
                    ))
                  }
                </Select>
              </Box>

              {
                requiresContractingCountry() &&
                <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Contracting Country</Typography>
                    <Autocomplete
                      disablePortal
                      disableClearable
                      options={countries}
                      sx={{
                        width: 170,
                        '& .MuiAutocomplete-input': { fontSize: 12 },
                        '& .MuiOutlinedInput-root': { height: 32, py: '0 !important' }
                      }}
                      size='small'
                      value={cignaGlobalHealthOptionSpecific['contracting_country'] || null}
                      onChange={(event, newValue) => handleSpecificChange('contracting_country', newValue || '')}
                      disabled={loadingStatus[id]}
                      renderInput={(params) => <TextField {...params} />}
                    />
                  </Box>
                  <Typography fontSize={11} sx={{ color: '#1976d2', mt: 0.5, maxWidth: 390 }}>
                    Contracting Country is required for Australia, Canada, and United States of America Country of Residence
                  </Typography>
                </Box>
              }

              {
                hasSameNationalityAndResidence() &&
                <Typography fontSize={11} sx={{ color: '#f44336', maxWidth: 390 }}>
                  Clients have the same Nationality and Country of Residence. To proceed with this quote, we need additional information about the nationality and country of residence of the policyholder. Please contact our sales support team on 971-4561-3804 or 1-877-539-6296.
                </Typography>
              }
            </Box>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Discount</Typography>
                <PercentInput
                  value={discounts[id] ?? 0}
                  onCommit={(value) => setDiscounts(prev => ({ ...prev, [id]: value }))}
                />
              </Box>

              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <Typography fontSize={13} fontWeight={600} sx={{ width: 170 }}>Renewal/Further Loading</Typography>
                <PercentInput
                  value={loadings[id] ?? 0}
                  onCommit={(value) => setLoadings(prev => ({ ...prev, [id]: value }))}
                />
              </Box>
            </Box>
          </Box>

          <div style={{ display: 'flex', alignItems: 'start', justifyContent: 'end', width: '100%', marginBottom: 7, marginTop: 24 }}>
            <CopyExcelDataButton
              elementId='table-cigna-global-health-option-rates'
              style={{ marginRight: 7, fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
            <CopyImageButton
              elementId='table-cigna-global-health-option-rates'
              setShowImageCopy={setShowImageCopy}
              style={{ fontSize: 11, padding: '2px 8px', minHeight: 0 }}
              sx={{ '& .MuiSvgIcon-root': { fontSize: 14 } }}
            />
          </div>

          <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
            <Table id='table-cigna-global-health-option-rates' size='small' style={{ minWidth: 650, background: '#fff', width: `calc(100% + ${clients.length > 5 ? clients.length - 5 : 0} * 75px)` }}>
              <TableHead>
                <TableRow style={{ background: '#00629B' }}>
                  <TableCell style={{ width: 260, color: '#fff', fontWeight: 600 }}>Plan</TableCell>
                  {
                    clients.map((client, idx) => {
                      if (client.full_name.trim() === '') {
                        return <TableCell style={{ width: 200, fontWeight: 600, color: '#fff' }} key={idx}>_</TableCell>
                      }

                      return <TableCell style={{ width: 200, color: '#fff', fontWeight: 600 }} key={idx}>
                        <div>{client.full_name} ({client.age})</div>
                        <div>{client.country_of_residence}</div>
                      </TableCell>
                    })
                  }
                  <TableCell style={{ color: '#fff', fontWeight: 600 }}>Subtotal</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {
                  tiers.map(({ tierId, label }) => (
                    <Fragment key={tierId}>
                      {
                        selectedPlans.includes(`${id}_${tierId}`) &&
                        <TableRow style={{ backgroundColor: '#ffffff' }}>
                          <TableCellText content={label} style={{ fontWeight: 600, color: '#000' }} bgColor='#b9f6ca' />
                          {
                            clients.map((client, idx) => (
                              <TableCellRate
                                key={client.uid}
                                rate={getInpatientFinalPremium(idx, tierId)}
                                currency={getCurrency()}
                                isLoading={loadingStatus[id]}
                                bgColor='#b9f6ca'
                              />
                            ))
                          }
                          <TableCellRate
                            rate={getSubtotal(clientIndex => getInpatientFinalPremium(clientIndex, tierId))}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#b9f6ca'
                          />
                        </TableRow>
                      }

                      {
                        categories.filter(({ key }) => key !== MANDATORY_CATEGORY_KEY).map(({ key, label: categoryLabel, field }) => (
                          <TableRatesRow
                            key={`${tierId}_${key}`}
                            planId={`${id}_${tierId}_${key}`}
                            planName={field(tierId)}
                            planLabel={categoryLabel}
                            clients={clients}
                            selectedPlans={selectedPlans}
                            loading={loadingStatus[id]}
                            rates={response ? response.rates : []}
                            currency={currency}
                            discount={discounts[id]}
                            additionalLoading={loadings[id]}
                            visible={selectedPlans.includes(`${id}_${tierId}`) && (key === MANDATORY_CATEGORY_KEY || selectedPlans.includes(`${id}_${key}`))}
                          />
                        ))
                      }

                      {
                        selectedPlans.includes(`${id}_${tierId}`) &&
                        <TableRow style={{ backgroundColor: '#ffffff' }}>
                          <TableCellText content={getPremiumLabel()} style={{ fontWeight: 600 }} bgColor='#dfe3ec' />
                          {
                            clients.map((client, idx) => (
                              <TableCellRate
                                key={client.uid}
                                rate={getFinalPremium(idx, tierId)}
                                currency={getCurrency()}
                                isLoading={loadingStatus[id]}
                                bgColor='#dfe3ec'
                              />
                            ))
                          }
                          <TableCellRate
                            rate={getSubtotal(clientIndex => getFinalPremium(clientIndex, tierId))}
                            currency={getCurrency()}
                            isLoading={loadingStatus[id]}
                            bgColor='#dfe3ec'
                          />
                        </TableRow>
                      }

                      {
                        selectedPlans.includes(`${id}_${tierId}`) &&
                        <TableRow>
                          <TableCell colSpan={clients.length + 2} style={{ border: 'none', padding: 0, height: 24, background: '#fff' }} />
                        </TableRow>
                      }
                    </Fragment>
                  ))
                }
              </TableBody>
            </Table>
          </TableContainer>

          <NotesDisplay appConfig={appConfig} insurerId={id} />

          <RatesVersionSelect
            insurer={id}
            label="Rates Version"
            insurerRates={insurerRates}
            selectedRatesVersion={selectedRatesVersion}
            setSelectedRatesVersion={setSelectedRatesVersion}
            disabled={loadingStatus[id]}
            handleFetchAppConfig={handleFetchAppConfig}
            onChange={() => setShowRefreshNow(true)}
          />
        </Box>
      </Paper>
    </Box>
  );
});

export default CignaGlobalHealthOption_Rates;
